import { XMarkIcon } from '@heroicons/react/24/outline';
import type { TimelineEvent } from '../../types';
import { formatRelativeTime } from './utils';
import { useEscapeKey } from '../../../../../hooks/useEscapeKey';

interface NewDxReportDraftModalProps {
    existingDraft: TimelineEvent;
    onResume: () => void;
    onStartFresh: () => void;
    onCancel: () => void;
}

/**
 * Prompt shown when the user clicks "New Dx report" but a stranded
 * in-progress draft (no submitted parent in the same group) already exists.
 * Lets them resume editing or discard and start fresh.
 */
export function NewDxReportDraftModal({
    existingDraft,
    onResume,
    onStartFresh,
    onCancel
}: NewDxReportDraftModalProps) {
    const timeAgo = formatRelativeTime(existingDraft.updatedAt || existingDraft.createdAt);

    useEscapeKey(onCancel, true);
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-[440px] p-6 relative">
                <div className="flex justify-between items-start mb-3">
                    <h3 className="text-base font-semibold text-gray-900">Unsaved draft found</h3>
                    <button
                        onClick={onCancel}
                        className="text-gray-400 hover:text-gray-700 transition-colors"
                        aria-label="Close"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                    You have an unsaved draft from {timeAgo}. Resume it or start fresh?
                </p>
                <div className="flex justify-end items-center gap-3">
                    <button
                        onClick={onStartFresh}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Start fresh
                    </button>
                    <button
                        onClick={onResume}
                        className="px-4 py-2 text-sm font-semibold text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                        Resume
                    </button>
                </div>
            </div>
        </div>
    );
}
